import React, { useState } from 'react';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../../../Styles/MCQAssessments/MCQAssessment.css';
import { toast } from '../toastUtils'; // Import toast utility

const AddAssessments = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: '',
    test_file: '',
    thumbnail: '',
    start_date_time: null,
    end_date_time: null,
    is_released: false,
    is_randomized: false,
  });
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [fileName, setFileName] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDateChange = (date, fieldName) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: date,
    }));
  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    setUploadStatus('');
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    
    if (file && name === 'test_file') {
      // Validate XLSX file
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        setFieldErrors((prev) => ({ 
          ...prev, 
          test_file: 'Please upload a valid XLSX file' 
        }));
        e.target.value = '';
        return;
      }
      setFileName(file.name);
    }
    
    setFormData((prevData) => ({
      ...prevData,
      [name]: file,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    
    // Validation
    if (!formData.test_file) {
      setFieldErrors({ test_file: 'Assessment file is required' });
      return;
    }
    
    if (!formData.start_date_time || !formData.end_date_time) {
      toast.warning('Validation Error', 'Please select both start and end date/time');
      return;
    }
    
    try {
      setUploadStatus('Uploading...');
      setUploadProgress(0);
  
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('duration', formData.duration);
      formDataToSend.append('test_file', formData.test_file);
      if (formData.thumbnail) {
        formDataToSend.append('thumbnail', formData.thumbnail);
      }
      formDataToSend.append('start_date_time', formData.start_date_time.toISOString());
      formDataToSend.append('end_date_time', formData.end_date_time.toISOString());
      formDataToSend.append('is_released', formData.is_released);
      formDataToSend.append('is_randomized', formData.is_randomized);
  
      const response = await axiosInstance.post('/assessments/tests/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });
  
      if (response.status === 201) {
        setUploadStatus('Assessment created successfully!');
        setUploadProgress(100);
        toast.success('Success!', 'Assessment created successfully!');
        
        // Reset form after short delay
        setTimeout(() => {
          setUploadStatus('');
          setUploadProgress(0);
          setFileName('');
          setFormData({
            name: '',
            description: '',
            duration: '',
            test_file: '',
            thumbnail: '',
            start_date_time: null,
            end_date_time: null,
            is_released: false,
            is_randomized: false,
          });
        }, 1500);
      }
    } catch (error) {
      setUploadStatus('');
      setUploadProgress(0);
      
      const backendData = error.response?.data || {};
      
      // Handle field-specific errors
      if (backendData.errors) {
        const errors = {};
        Object.keys(backendData.errors).forEach(key => {
          if (Array.isArray(backendData.errors[key])) {
            errors[key] = backendData.errors[key].join(', ');
          } else {
            errors[key] = backendData.errors[key];
          }
        });
        setFieldErrors(errors);
      }
      
      const message = backendData.message || 'An error occurred while creating the assessment.';
      const nonFieldErrors = backendData.errors?.non_field_errors?.join(', ') || '';
  
      let errorText = message;
      if (nonFieldErrors) {
        errorText += ` ${nonFieldErrors}`;
      }
  
      toast.error('Error!', errorText);
    }
  };
  
  return (
    <div className="add-assessments-container">
      <div className="add-assessments-header">
        <h2 className="add-assessments-title">Create New Assessment</h2>
        <p className="add-assessments-subtitle">Fill in the details below to create a new assessment</p>
      </div>
      
      {uploadStatus && (
        <div className="upload-feedback">
          <div className="upload-message">{uploadStatus}</div>
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      <form className="add-assessments-form-add" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Assessment Name <span className="required-star">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter assessment name"
              required
              className={fieldErrors.name ? 'input-error' : ''}
            />
            {fieldErrors.name && <span className="error-message">{fieldErrors.name}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="duration">Duration (minutes) <span className="required-star">*</span></label>
            <input
              type="number"
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              placeholder="60"
              required
              min="1"
              className={fieldErrors.duration ? 'input-error' : ''}
            />
            {fieldErrors.duration && <span className="error-message">{fieldErrors.duration}</span>}
          </div>
        </div>

        <div className="form-group form-group-full">
          <label htmlFor="description">Description <span className="required-star">*</span></label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter assessment description"
            required
            rows="3"
            className={fieldErrors.description ? 'input-error' : ''}
          />
          {fieldErrors.description && <span className="error-message">{fieldErrors.description}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_date_time">Start Date & Time <span className="required-star">*</span></label>
            <DatePicker
              selected={formData.start_date_time}
              onChange={(date) => handleDateChange(date, 'start_date_time')}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMM d, yyyy h:mm aa"
              placeholderText="Select start date and time"
              className="custom-date-picker"
              minDate={new Date()}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_date_time">End Date & Time <span className="required-star">*</span></label>
            <DatePicker
              selected={formData.end_date_time}
              onChange={(date) => handleDateChange(date, 'end_date_time')}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMM d, yyyy h:mm aa"
              placeholderText="Select end date and time"
              className="custom-date-picker"
              minDate={formData.start_date_time || new Date()}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="file">Assessment File <span className="required-star">*</span></label>
            <div className="file-upload-wrapper">
              <input
                type="file"
                id="file"
                name="test_file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                required
                className={`file-input ${fieldErrors.test_file ? 'input-error' : ''}`}
              />
              {fileName && <div className="file-name-display">{fileName}</div>}
            </div>
            {fieldErrors.test_file && <span className="error-message">{fieldErrors.test_file}</span>}
            <small className="field-hint">XLSX or XLS format only</small>
          </div>

          <div className="form-group">
            <label htmlFor="thumbnail">Thumbnail (Optional)</label>
            <div className="file-upload-wrapper">
              <input
                type="file"
                id="thumbnail"
                name="thumbnail"
                accept="image/*"
                onChange={handleFileChange}
                className={`file-input ${fieldErrors.thumbnail ? 'input-error' : ''}`}
              />
            </div>
            {fieldErrors.thumbnail && <span className="error-message">{fieldErrors.thumbnail}</span>}
            <small className="field-hint">PNG or JPG, 1200x630px recommended</small>
          </div>
        </div>

        <div className="form-row toggle-row">
          <div className="form-group toggle-group">
            <div className="toggle-label-group">
              <label htmlFor="is_released">Release Immediately</label>
              <small className="field-hint">Make available to users now</small>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                id="is_released"
                name="is_released"
                checked={formData.is_released}
                onChange={handleChange}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-group toggle-group">
            <div className="toggle-label-group">
              <label htmlFor="is_randomized">Randomize Questions</label>
              <small className="field-hint">Shuffle question order</small>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                id="is_randomized"
                name="is_randomized"
                checked={formData.is_randomized}
                onChange={handleChange}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className={`submit-button ${uploadStatus === 'Uploading...' ? 'btn-loading' : ''}`}
            disabled={uploadStatus === 'Uploading...'}
          >
            <span>
              {uploadStatus === 'Uploading...' 
                ? `Uploading... ${uploadProgress > 0 ? `${uploadProgress}%` : ''}`
                : 'Create Assessment'
              }
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddAssessments;
